import { useCallback, useEffect, useState } from 'react';
import { setUserAgreementStatus } from '../../api/user';

// components
import Terms from './Terms';

// mui
import { useMediaQuery } from "@mui/material";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Modal from '@mui/material/Modal';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// types
type propsType = {
    onClose: () => void,
    isOpen: boolean,
    userId: number,
}

export default function TermsModal(props: propsType) {
    const {onClose, isOpen, userId} = props;
    const isTabletSizeUp = useMediaQuery("@media(min-width:768px)");

    /**
     * 利用規約の同意
     */
    const [agreementValue, setAgreementValue] = useState<"agree" | "disagree">("disagree");

    const handleChangeAgreement = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgreementValue(event.target.value as "agree" | "disagree");
    }

    /**
     * 利用規約コンテナを最後までスクロールしたら同意をチェックできるようにする
     */
    const [isReadTerms, setIsReadTerms] = useState<boolean>(false);
    const [frameCurrent, setFrameCurrent] = useState<HTMLDivElement | null>(null);
    const [containerCurrent, setContainerCurrent] = useState<HTMLDivElement | null>(null);
    const setFrameRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            setFrameCurrent(node);
        }
    }, []);
    const setContainerRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            setContainerCurrent(node);
        }
    }, []);

    useEffect(() => {
        if (frameCurrent !== null && containerCurrent !== null) {
            const onScroll = () => {
                const position = frameCurrent.scrollTop;
                const heightFrame = frameCurrent.clientHeight;
                const heightContainer = containerCurrent.clientHeight;
                // 端数で正常動作しない場合があるので10引いておく
                if (position >= heightContainer - heightFrame - 10) {
                    setIsReadTerms(true);
                }
            };
            frameCurrent.addEventListener("scroll", onScroll);

            // クリーンアップ関数でイベントリスナーを削除
            return () => {
                frameCurrent.removeEventListener("scroll", onScroll);
            };
        }
    }, [frameCurrent, containerCurrent]);

    /**
     * 値の送信
     */
    const fnOnClickSend = (agreementValue: "agree" | "disagree") => {
        if (userId) {
            setUserAgreementStatus(userId, agreementValue)
                .then((result) => {
                    if (result !== "success") return false;
                    if (agreementValue === "agree") {
                        onClose();
                    }
                    if (agreementValue === "disagree") {
                        window.location.href = "https://service.opexpark.co.jp/";
                    }
                })
        }
    }

    return (
        <Box sx={{position: "relative"}}>
            <Modal
                open={isOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute' as 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: "calc(100% - 32px)",
                    maxWidth: "980px",
                    maxHeight: "90vh",
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 24,
                    boxSizing: 'border-box',
                    overflowY: "auto",
                    '&:focus-visible': {
                        outline: "none",
                    },
                }}>
                    <Box component="section">
                        <Typography variant="subtitle1" component="h2" fontWeight="600">
                            個人情報の取り扱いについて
                        </Typography>
                        <Typography paragraph>
                            私たちが取り扱う全ての個人情報の保護について、社会的使命を十分に認識し、本人の権利の保護、個人情報に関する法規制等を遵守致します。<br />
                            なお、当社の個人情報保護に関する基本方針については、<Link href="https://www.opexpark.co.jp/privacy/" target="_blank">プライバシーポリシー</Link>をご確認ください。
                        </Typography>
                    </Box>

                    <Box component="section">
                        <Typography variant="subtitle1" component="h2" fontWeight="600">
                            利用規約について
                        </Typography>
                        <Typography paragraph>
                            利用規約をスクロールして最後までご確認の上、同意いただける場合は「同意する」にチェックを入れてください。
                        </Typography>
                        <Box
                            ref={setFrameRef}
                            sx={{
                                maxHeight: "35vh",
                                p: 2,
                                overflowY: "auto",
                                bgcolor: (theme) => theme.palette.grey[100],
                            }}
                        >
                            <Box ref={setContainerRef}>
                                <Terms />
                            </Box>
                        </Box>
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            direction={isTabletSizeUp ? "row" : "column"}
                            spacing={isTabletSizeUp ? 5 : 1}
                            sx={{ mt: 2, width: "100%" }}
                        >
                            <FormControl
                                disabled={!isReadTerms}
                            >
                                <RadioGroup
                                    aria-label="gender"
                                    defaultValue="open"
                                    name="radio-buttons-group"
                                    onChange={handleChangeAgreement}
                                    row
                                    sx={{
                                        justifyContent: "center",
                                    }}
                                    value={agreementValue}
                                >
                                    <FormControlLabel value="disagree" control={<Radio />} label="同意しない" />
                                    <FormControlLabel value="agree" control={<Radio />} label="同意する" sx={{mr:0}} />
                                </RadioGroup>
                            </FormControl>

                            <Button
                                variant="contained"
                                disabled={!isReadTerms}
                                size={isTabletSizeUp ? "small" : "medium"}
                                onClick={() => fnOnClickSend(agreementValue)}
                            >
                                送信
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}